import Vue from "vue";
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import isBoolean from "lodash/isBoolean";
import isObject from "lodash/isObject";
import isString from "lodash/isString";
import { OPERATIONS } from "@/config/const";
const { INPUT_TYPES: it, INPUT_TYPE_ENUM: ite } = OPERATIONS;
const enterpriseFormApi = AiseaApiFactory.get("enterpriseForm");
import operationReportService from "@/services/operations/operationReportService";

/**
 * Service class for handling enterprise forms.
 */
class EnterpriseFormService {
  /**
   * APIで法人に紐づくForm一覧を取得する
   * @returns {Promise<Object|null>} 操作設定のオブジェクトまたはnull
   * @property {boolean} status - APIのリクエストが成功したかどうか
   * @property {number} code - レスポンスのステータスコード
   * @property {EnterpriseForm[]} data - レスポンスのデータ
   * @property {string} message - レスポンスのメッセージ
   * @throws {Error} - エラーが発生した場合、エラーをスローする
   */
  async getEnterpriseForms() {
    return await enterpriseFormApi.index();
  }

  validEnterpriseForms(enterpriseForms) {
    if (!Array.isArray(enterpriseForms)) return [];
    return enterpriseForms.filter(ef => {
      const isEnterpriseForm = this.isEnterpriseForm(ef);
      return isEnterpriseForm;
    });
  }

  enterpriseFormVersions(enterpriseForms) {
    return this.validEnterpriseForms(enterpriseForms)
      .map(ef => ef.form_versions)
      .flat()
      .filter(efv => {
        const isEnterpriseFormVersion = this.isEnterpriseFormVersion(efv);
        if (!isEnterpriseFormVersion) {
          Vue.$log.warn("Invalid EnterpriseFormVersion", efv);
        }
        return isEnterpriseFormVersion;
      });
  }

  enterpriseFormVersionItems(enterpriseFormVersions) {
    if (!Array.isArray(enterpriseFormVersions)) return [];
    return enterpriseFormVersions
      .filter(efv => this.isEnterpriseFormVersion(efv))
      .map(ef => ef.form_version_items)
      .flat()
      .filter(eFVI => {
        const isEnterpriseFormVersionItem = this.isEnterpriseFormVersionItem(
          eFVI
        );
        return isEnterpriseFormVersionItem;
      });
  }

  /**
   * Check if an object is an EnterpriseForm
   * @param {any} obj - The object to check
   * @returns {boolean} - True if the object is an EnterpriseForm, false otherwise
   */
  isEnterpriseForm(obj) {
    if (!isObject(obj)) return false;
    return isString(obj.id) && Array.isArray(obj.form_versions);
  }

  /**
   * Check if an object is an EnterpriseFormVersion
   * @param {any} obj - The object to check
   * @returns {boolean} - True if the object is an EnterpriseFormVersion, false otherwise
   */
  isEnterpriseFormVersion(obj) {
    if (!isObject(obj)) return false;
    return (
      isString(obj.id) &&
      Number.isInteger(obj.version_no) &&
      isString(obj.start_at) &&
      Array.isArray(obj.form_version_items)
    );
  }

  /**
   * INPUT_TYPES.DATE_PICKER, // 1: DATE_PICKER (年月日)
   * Checks if the input type is a date picker.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form object.
   * @param {number} enterpriseFormVersionItem.input_type - The input type to check.
   * @returns {boolean} Returns true if the input type is a date picker, otherwise false.
   */
  isDatePickers(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.DATE_PICKER;
  }

  /**
   * Retrieves the input type from the given enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @returns {string | undefined} - The input type of the form version item.
   */
  getInputTypeFromFormVersionItem(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    return ite[eFVI.input_type];
  }

  /**
   * INPUT_TYPES.TIME_PICKER, // 2: TIME_PICKER (時間)
   * Checks if the input type is a time picker.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form object.
   * @param {number} enterpriseFormVersionItem.input_type - The input type to check.
   * @returns {boolean} Returns true if the input type is a time picker, otherwise false.
   */
  isTimePickers(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.TIME_PICKER;
  }

  /*
   * INPUT_TYPES.DATETIME_PICKER, // 3: SPACE （レイアウト調整用）
   * Checks if the input type of the enterprise form is a space.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form object.
   * @param {number} enterpriseFormVersionItem.input_type - The input type to check.
   * @returns {boolean} - Returns true if the input type is a space, otherwise false.
   */
  isSpace(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.SPACE;
  }

  /*
   * INPUT_TYPES.DATETIME_PICKER, // 4: DATETIME_PICKER (日時)
   * Checks if the input type of the enterprise form is a date-time picker.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form object.
   * @param {number} enterpriseFormVersionItem.input_type - The input type to check.
   * @returns {boolean} - Returns true if the input type is a date-time picker, otherwise false.
   */
  isDateTimePickers(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.DATETIME_PICKER;
  }

  /**
   * Checks if the given enterprise form has a valid format.
   *
   * @param {object} enterpriseFormVersionItem - The enterprise form object to check.
   * @returns {boolean} - Returns true if the enterprise form has a valid format, otherwise returns false.
   */
  hasFormat(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    return (
      (this.isDatePickers(eFVI) ||
        this.isTimePickers(eFVI) ||
        this.isDateTimePickers(eFVI)) &&
      isString(eFVI.format)
    );
  }

  /**
   * INPUT_TYPES.SINGLE_IMAGE_FILE, // 12: SINGLE_IMAGE_FILE (1ファイル(画像)アップロード)
   * Checks if the given enterprise form version item is a single image file.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item to check.
   * @param {number} enterpriseFormVersionItem.input_type - The input type to check.
   * @returns {boolean} - Returns true if the enterprise form version item is a single image file, false otherwise.
   */
  isSingleImageFile(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.SINGLE_IMAGE_FILE;
  }

  //   INPUT_TYPES.SELECT, // 5: SELECT (選択項目)
  isSelect(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.SELECT;
  }

  //   INPUT_TYPES.CHECK_BOX, // 7: CHECK_BOX (チェックボックス)
  isCheckbox(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.CHECK_BOX;
  }

  //   INPUT_TYPES.RADIO_BUTTON, // 8: RADIO_BUTTON (ラジオボタン)
  isRadioButton(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.RADIO_BUTTON;
  }

  //   INPUT_TYPES.NUMBER, // 9: NUMBER (数値入力)
  isNumber(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.NUMBER;
  }

  //   INPUT_TYPES.SHORT_TEXT, // 10: SHORT_TEXT  (短いテキスト入力)
  isShortText(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.SHORT_TEXT;
  }

  //   INPUT_TYPES.LONG_TEXT, // 11: LONG_TEXT  (長いテキスト入力)
  isLongText(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.LONG_TEXT;
  }

  /**
   * Returns the value type based on the given enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @returns {string} The value type.
   * @throws {Error} If the value type is invalid.
   */
  getValueType(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    // 未使用 // 0: FIXED (固定値)
    if (this.isDatePickers(eFVI)) return "value_date"; // 1: DATE_PICKER (年月日)
    if (this.isTimePickers(eFVI)) return "value_time"; // 2: TIME_PICKER (時間)
    if (this.isSpace(eFVI)) return null; // 3: SPACE（レイアウト調整用）
    if (this.isDateTimePickers(eFVI)) return "value_date_time"; // 4: DATETIME_PICKER (日時)
    if (this.isSelect(eFVI)) return "value_string"; // 5: SELECT (選択項目)
    // 未使用 // 6: HIERARCHICAL_SELECT (２階層選択)
    if (this.isCheckbox(eFVI)) return "value_string"; // 7: CHECK_BOX (チェックボックス)
    if (this.isRadioButton(eFVI)) return "value_string"; // 8: RADIO_BUTTON (ラジオボタン)
    if (this.isNumber(eFVI)) return "value_number"; // 9: NUMBER (数値入力)
    if (this.isShortText(eFVI)) return "value_string"; // 10: SHORT_TEXT  (短いテキスト入力)
    if (this.isLongText(eFVI)) return "value_string"; // 11: LONG_TEXT  (長いテキスト入力)
    if (this.isSingleImageFile(eFVI)) return "value_files"; // 12: SINGLE_IMAGE_FILE (1ファイル(画像)アップロード)
    // 未使用 // 13: SINGLE_OTHER_FILE（1ファイル(画像以外)アップロード)
    throw new Error("Invalid value type"); // WARNING:
  }

  /**
   * Checks if the given enterprise form version item has a unit.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item to check.
   * @returns {boolean} - Returns true if the enterprise form version item has a unit, false otherwise.
   */
  hasUnit(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;
    return isString(eFVI.unit);
  }

  /**
   * Retrieves the unit from the given enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @returns {string} The unit of the enterprise form version item, or an empty string if it doesn't have a unit.
   */
  getUnit(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.hasUnit(eFVI)) return "";
    return eFVI.unit;
  }

  /**
   * Checks if the given enterprise form version item has select options.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item to check.
   * @returns {boolean} - Returns true if the enterprise form version item has select options, false otherwise.
   */
  hasSelectOptions(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;
    return Array.isArray(eFVI.select_options) && eFVI.select_options.length > 0;
  }

  /**
   * Retrieves the select options from the given enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @returns {Array} - The select options. EnterpriseFormVersionItemSelectOption
   */
  getSelectOptions(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.hasSelectOptions(eFVI)) return [];
    return eFVI.select_options;
  }

  /**
   * Retrieves the label from an enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @returns {string} The label of the enterprise form version item.
   */
  getLabelFromEnterpriseFormVersionItem(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return "";
    return eFVI.label;
  }

  /**
   * Retrieves the 'required' property from the given enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item to retrieve the 'required' property from.
   * @returns {boolean} - The value of the 'required' property from the enterprise form version item.
   */
  getRequiredFromEnterpriseFormVersionItem(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;
    return eFVI.required;
  }

  /**
   * Retrieves the nullable label from an enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @returns {string} The nullable label.
   */
  getNullableLabelFromEnterpriseFormVersionItem(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return "";
    return eFVI.nullable_label;
  }

  /**
   * Retrieves the default value from the given enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @returns {string} The default value of the enterprise form version item.
   */
  getDefaultValue(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return "";
    return eFVI.default_value;
  }

  hasDefaultValue(enterpriseFormVersionItem) {
    const defaultValue = this.getDefaultValue(enterpriseFormVersionItem);
    const hasDefaultValue = isString(defaultValue) && defaultValue !== "";
    return hasDefaultValue;
  }

  /**
   * Retrieves the default value ID from the given enterprise form version item.
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @returns {string} The default value ID. // operationReportFormVersionItemのid
   */
  getDefaultValueId(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return "";
    return eFVI.default_value_id;
  }

  /**
   * Retrieves the default enterprise form version item by the default value ID from the given enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @param {Object} reportFormVersionItemMap - The map of report form version object.
   * @returns {AoEnterpriseVesselReportFormVersionItem} The default report form version item.
   */
  getDefaultReportFormVersion(
    enterpriseFormVersionItem,
    reportFormVersionItemMap
  ) {
    const eFVI = enterpriseFormVersionItem;
    const rFVIMap = reportFormVersionItemMap;
    if (!isObject(rFVIMap)) return null;

    const defaultValueId = this.getDefaultValueId(eFVI);
    const defaultFormVersionItem = rFVIMap[defaultValueId];
    return defaultFormVersionItem;
  }

  getDefaultValueByDefaultValueId(
    enterpriseFormVersionItem,
    reportFormVersionItemMap
  ) {
    const eFVI = enterpriseFormVersionItem;
    const rFVIMap = reportFormVersionItemMap;
    const defaultFormVersionItem = this.getDefaultReportFormVersion(
      eFVI,
      rFVIMap
    );
    const defaultValue = operationReportService.getValueFromReportFormVersionItem(
      defaultFormVersionItem
    );
    return defaultValue;
  }

  /**
   * Check if an object is an EnterpriseFormVersionItem
   * @param {any} obj - The object to check
   * @returns {boolean} - True if the object is an EnterpriseFormVersionItem, false otherwise
   */
  isEnterpriseFormVersionItem(obj) {
    if (!isObject(obj)) return false;
    const isEnterpriseFormVersionItem =
      isString(obj.id) &&
      isString(obj.label) &&
      Number.isInteger(obj.input_type) &&
      (isString(obj.format) || obj.format === null) &&
      (isString(obj.default_value) || obj.default_value === null) &&
      (isString(obj.default_value_id) || obj.default_value_id === null) &&
      (Array.isArray(obj.select_options) || obj.select_options === null) && // select_optionsの中身までは見ていない
      (isString(obj.unit) || obj.unit === null) &&
      (Number.isInteger(obj.caution_min) || obj.caution_min === null) &&
      (Number.isInteger(obj.warning_min) || obj.warning_min === null) &&
      isBoolean(obj.required) &&
      (isString(obj.nullable_label) || obj.nullable_label === null) &&
      Number.isInteger(obj.sort_order);
    if (!isEnterpriseFormVersionItem) {
      Vue.$log.warn("Invalid EnterpriseFormVersionItem", obj);
    }
    return isEnterpriseFormVersionItem;
  }
}

export default new EnterpriseFormService();
